import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Snackbar,
  Alert,
  MenuItem,
} from '@mui/material';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const defaultForm = {
  name: '',
  description: '',
  sort_order: 0,
  is_active: 1,
};

export default function StudentPreferences() {
  const [preferences, setPreferences] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchPreferences = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/admin/student-preferences`);
      setPreferences(res.data?.data || []);
    } catch (_err) {
      showSnackbar('Failed to load student preferences', 'error');
    }
  };

  useEffect(() => {
    fetchPreferences();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(defaultForm);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      description: row.description || '',
      sort_order: row.sort_order || 0,
      is_active: Number(row.is_active) ? 1 : 0,
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      if (!form.name) {
        showSnackbar('Preference name is required', 'error');
        return;
      }

      if (editing) {
        await axios.put(`${BASE_URL}/api/admin/student-preferences/${editing.id}`, form);
        showSnackbar('Preference updated');
      } else {
        await axios.post(`${BASE_URL}/api/admin/student-preferences`, form);
        showSnackbar('Preference created');
      }

      setOpen(false);
      fetchPreferences();
    } catch (_err) {
      showSnackbar('Failed to save preference', 'error');
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/api/admin/student-preferences/${id}`);
      showSnackbar('Preference deleted');
      fetchPreferences();
    } catch (_err) {
      showSnackbar('Failed to delete preference', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Student Preferences</Typography>
        <Button variant="contained" onClick={openCreate}>Add Preference</Button>
      </Box>

      <Card>
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Order</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preferences.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.description || '-'}</TableCell>
                  <TableCell>{row.sort_order}</TableCell>
                  <TableCell>
                    <Chip
                      label={Number(row.is_active) ? 'Active' : 'Inactive'}
                      size="small"
                      color={Number(row.is_active) ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" variant="outlined" onClick={() => openEdit(row)}>Edit</Button>
                      <Button size="small" variant="outlined" color="error" onClick={() => remove(row.id)}>Delete</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Update Preference' : 'Create Preference'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            <TextField label="Description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} multiline rows={3} />
            <TextField label="Sort Order" type="number" value={form.sort_order} onChange={(e) => setForm((p) => ({ ...p, sort_order: Number(e.target.value) }))} />
            <TextField
              select
              label="Status"
              value={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: Number(e.target.value) }))}
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submit}>{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
